import { ReactElement, useMemo, useState } from "react";
import {
  SxProps,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import StyledTable, { StyledTableProps } from "./StyledTable";

export type SortDirection = "asc" | "desc";

type ColumnOption<TRow> = {
  key: string;
  label: string;
  defaultSortDirection?: SortDirection;
  align?: "left" | "right" | "center";
  renderCell?: (row: TRow) => React.ReactNode;
  sortBy?: (row: TRow) => string | number | boolean;
  dense?: boolean;
  disableSort?: boolean;
};

export type ColumnOptions<T> = readonly ColumnOption<T>[];

type Props<T> = {
  data: T[];
  columns: ColumnOptions<T>;
  getKey: (row: T) => string | number;
  rowStyles?: (row: T) => SxProps;
  hoverable?: boolean;
  sort?: { sortBy: ColumnOption<T>["key"]; sortDir: SortDirection };
  onSortChange?: (sortBy: ColumnOption<T>["key"], sortDir: SortDirection) => void;
  defaultSortBy?: ColumnOption<T>["key"];
  emptyMessage?: string;
  onClickRow?: (row: T) => void;
  sortMode?: "client" | "server";
} & StyledTableProps;

function DataTable<T>(props: Props<T>): ReactElement {
  const {
    columns,
    data,
    getKey,
    rowStyles,
    hoverable,
    sort,
    onSortChange,
    onClickRow,
    defaultSortBy,
    emptyMessage,
    sortMode = "client",
    ...tableProps
  } = props;
  const [sortBy, setSortBy] = useState(defaultSortBy || columns[0].key);
  const [sortDir, setSortDir] = useState<SortDirection>(
    columns.find((col) => col.key === sortBy)?.defaultSortDirection || "asc"
  );

  // Controlled externally if sort prop is provided, otherwise use local state
  const _sortBy = !!sort ? sort.sortBy : sortBy;
  const _sortDir = !!sort ? sort.sortDir : sortDir;

  const sortedData = useMemo(() => {
    if (sortMode === "server") return data;

    return [...data].sort((a, b) => {
      const column = columns.find((col) => col.key === _sortBy);
      if (!column) {
        throw new Error(`Column with key "${String(_sortBy)}" not found`);
      }
      const aValue = column.sortBy?.(a) ?? a[_sortBy];
      const bValue = column.sortBy?.(b) ?? b[_sortBy];

      // Put empty values last
      if (!!aValue && !bValue) return _sortDir === "asc" ? -1 : 1;
      if (!aValue && !!bValue) return _sortDir === "asc" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return _sortDir === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        const aNumber = Number(aValue) || 0;
        const bNumber = Number(bValue) || 0;
        return _sortDir === "asc" ? aNumber - bNumber : bNumber - aNumber;
      }
    });
  }, [columns, data, _sortBy, _sortDir, sortMode]);

  const updateSortOptions = (column: ColumnOption<T>) => {
    let newSortDir: SortDirection = "asc";
    if (_sortBy === column.key) {
      newSortDir = _sortDir === "asc" ? "desc" : "asc";
      setSortDir(newSortDir);
    } else {
      newSortDir = column.defaultSortDirection || "asc";
      setSortBy(column.key);
      setSortDir(newSortDir);
    }
    onSortChange?.(column.key, newSortDir);
  };

  return (
    <TableContainer>
      <StyledTable size="small" {...tableProps}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key as string}
                align={column.align || "left"}
                sx={[{ fontWeight: "bold" }, !!column.dense && { width: 0, whiteSpace: "nowrap" }]}
              >
                {column.disableSort ? (
                  column.label
                ) : (
                  <TableSortLabel
                    onClick={() => updateSortOptions(column)}
                    active={_sortBy === column.key}
                    direction={_sortBy === column.key ? _sortDir : "asc"}
                  >
                    {column.label}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ fontStyle: "italic" }}>
                {emptyMessage || "Inga data att visa"}
              </TableCell>
            </TableRow>
          )}

          {sortedData.map((row) => {
            return (
              <TableRow
                key={getKey(row)}
                sx={rowStyles?.(row)}
                hover={hoverable}
                onClick={() => props.onClickRow?.(row)}
              >
                {columns.map((column) => {
                  return (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={[!!column.dense && { width: 0, whiteSpace: "nowrap" }]}
                    >
                      {column.renderCell?.(row) ?? row[column.key]}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
}

export default DataTable;
